import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Button,
} from "@b2b/ui-components";
import { adminService, getAccessToken } from "@b2b/services";

interface Activity {
  id: string;
  type: string;
  user: string;
  action: string;
  timestamp: string;
  status: string;
  profileImage?: string | null;
  companyLogo?: string | null;
}

interface AdminRecentActivityProps {
  activities?: Activity[];
  loading?: boolean;
  error?: string | null;
}

const initialsFromUser = (user: string) =>
  user
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

/** Primary avatar: profile photo when set, otherwise company logo from registration. */
const primaryActivityImage = (activity: Activity) =>
  activity.profileImage?.trim() || activity.companyLogo?.trim() || undefined;

const showCompanyLogoBadge = (activity: Activity) =>
  Boolean(
    activity.profileImage?.trim() &&
      activity.companyLogo?.trim() &&
      activity.profileImage.trim() !== activity.companyLogo.trim()
  );

const getStatusColor = (status: string) => {
  switch (status) {
    case "pending":
      return "warning";
    case "approved":
    case "completed":
    case "verified":
      return "success";
    case "rejected":
      return "destructive";
    default:
      return "secondary";
  }
};

export function AdminRecentActivity({
  activities: propActivities,
  loading: propLoading,
  error: propError,
}: AdminRecentActivityProps = {}) {
  const itemsPerPage = 10;
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(!propActivities);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    // If activities are provided as prop, use them
    if (propActivities) {
      setActivities(propActivities);
      setLoading(false);
      return;
    }

    // Otherwise, fetch from API
    // Wait for token to be available before making API call (prevents cancellation for Auth0 users)
    const fetchActivities = async () => {
      // Check if token exists before making API call
      const token = await getAccessToken();
      if (!token) {
        console.log("⏳ AdminRecentActivity: Token not available yet, waiting...");
        // Retry after a short delay
        setTimeout(() => {
          fetchActivities();
        }, 500);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log("📞 AdminRecentActivity: Fetching recent activity...");
        const data = await adminService.getRecentActivity();
        setActivities(data);
        console.log("✅ AdminRecentActivity: Recent activity fetched successfully");
      } catch (err: any) {
        console.error("❌ AdminRecentActivity: Error fetching recent activity:", err);
        setError(err.message || "Failed to load recent activity");
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [propActivities]);

  const isLoading = propLoading !== undefined ? propLoading : loading;
  const displayError = propError !== undefined ? propError : error;
  const displayActivities = propActivities || activities;
  const totalPages = Math.ceil(displayActivities.length / itemsPerPage);
  const paginatedActivities = displayActivities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [displayActivities.length]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            <span className="ml-2 text-gray-600">Loading activities...</span>
          </div>
        ) : displayError ? (
          <div className="text-center py-8 text-red-600">
            <p className="text-sm font-medium">Error loading activities</p>
            <p className="text-xs mt-1">{displayError}</p>
          </div>
        ) : displayActivities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No recent activity</p>
          </div>
        ) : (
          <>
            {paginatedActivities.map((activity) => {
              const primarySrc = primaryActivityImage(activity);
              const companyBadgeSrc =
                showCompanyLogoBadge(activity) && activity.companyLogo
                  ? activity.companyLogo.trim()
                  : null;

              return (
                <div
                  key={activity.id}
                  className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="relative shrink-0">
                    <Avatar className="h-8 w-8">
                      {primarySrc ? (
                        <AvatarImage
                          src={primarySrc}
                          alt=""
                          className="object-cover"
                        />
                      ) : null}
                      <AvatarFallback className="bg-gray-100 text-orange-500 text-xs">
                        {initialsFromUser(activity.user)}
                      </AvatarFallback>
                    </Avatar>
                    {companyBadgeSrc ? (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 overflow-hidden rounded border-2 border-white bg-white shadow-sm ring-1 ring-gray-200"
                        title="Company logo"
                      >
                        <img
                          src={companyBadgeSrc}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </span>
                    ) : null}
                  </div>

                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm">
                        <span className="font-medium">{activity.user}</span>{" "}
                        <span className="text-gray-500">{activity.action}</span>
                      </p>
                      <Badge
                        variant={getStatusColor(activity.status) as any}
                        className="text-xs"
                      >
                        {activity.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(activity.timestamp), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </div>
              );
            })}

            {displayActivities.length > itemsPerPage && (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="min-w-24"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
                <p className="text-sm font-medium text-gray-600">
                  Page {currentPage} of {totalPages}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-w-24"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
